import type { Match } from '../model/types';

/**
 * Приём открыт — решает сервер (`isOpenForPredictions`), а не сравнение
 * времени на клиенте: часы устройства врут, а после первой отсечки приём
 * не открывается повторно (правило 7 в CLAUDE.md бэкенда).
 */
export function isAcceptingPredictions(match: Match): boolean {
  return match.isOpenForPredictions;
}

/**
 * Есть ли что открыть тапом по карточке — и что именно.
 *
 * Витрины две, и открывают они РАЗНОЕ: «Текущие» — шторку Прогноза,
 * «Завершённые» — статистику по своим Прогнозам. Карточка не знает,
 * в какой она ленте, поэтому выбор не её: `MatchCard` кликается ровно
 * тогда, когда витрина дала ему `onClick`, а даёт она его по этим двум
 * предикатам.
 *
 * Пока предикаты жили внутри карточки через «или», в «Текущих» кликался
 * матч, ждущий результата: приём у него закрыт, Прогноз есть — значит
 * `canOpenStats`. Открывалась при этом шторка Прогноза, а она при пустом
 * наборе типов не рисует ничего, и игрок получал прозрачный оверлей
 * во весь экран: скролл заблокирован, закрыть нечем.
 */
export function canOpenPredictionForm(match: Match): boolean {
  return match.isOpenForPredictions && match.availableTypes.length > 0;
}

/**
 * Статистика показывает, что зашло, а что нет, — она нужна на сыгранном
 * матче со своими Прогнозами. Приём при этом обязан быть закрыт: пока он
 * открыт, тап значит «поставить ещё», а не «посмотреть итог».
 */
export function canOpenStats(match: Match): boolean {
  return !match.isOpenForPredictions && match.myPredictions.length > 0;
}

export function getMatchTitle(match: Match): string {
  const home = match.homeTeam.shortName ?? match.homeTeam.name;
  const away = match.awayTeam.shortName ?? match.awayTeam.name;
  return `${home} — ${away}`;
}

export function hasScore(match: Match): boolean {
  return match.homeScore !== undefined && match.awayScore !== undefined;
}

export type MatchResult = 'success' | 'failure' | null;

/**
 * Итог Прогнозов на матч для карточки «Завершённые» (узлы 438:51166
 * «Успех» / 438:51167 «Провал») — читает `status` Прогноза, который
 * считает сервер (WON/VOID_WON/LOST), а не сравнивает счёт на клиенте.
 *
 * **Смешанный матч показывается «Успехом».** На матч бывает несколько
 * типов Прогноза с разным исходом, и кит такую комбинацию не рисует.
 * До 01.09.2026 приоритет был обратный — один проигранный тип метил всю
 * карточку «Провалом», из осторожности «смолчать про проигрыш опаснее».
 * Владелец решил иначе: выиграла хоть одна ставка — «Успех» и Очки.
 * Так и честнее: Очки за выигравший тип игроку начислены, а карточка
 * с «Провалом» и ненулевыми Очками противоречила бы сама себе.
 */
export function getMatchResult(match: Match): MatchResult {
  const statuses = match.myPredictions.map((prediction) => prediction.status);
  if (statuses.some((status) => status === 'WON' || status === 'VOID_WON')) {
    return 'success';
  }
  if (statuses.includes('LOST')) return 'failure';
  return null;
}
